import type { BoxProps } from '@chakra-ui/react'
import { Box, Text } from '@chakra-ui/react'
import React from 'react'
import { useThemeColors } from 'hooks/useThemeColors'
import { TEXT_CODE_PROPS } from 'lib/chakra/constants'

type SourceCodeProps = {
  code: string
  filename: string
} & BoxProps

const SNIPPET_HEIGHT = 450
const BORDER_RADIUS = 10

export const SourceCode = ({ code, filename, ...props }: SourceCodeProps) => {
  const COLORS = useThemeColors()
  const lineCounterRef = React.useRef<HTMLDivElement>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const handleTextareaScroll = () => {
    if (lineCounterRef.current && textareaRef.current) {
      lineCounterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  return (
    <Box {...props}>
      <Text mb={5} variant="text2medium">
        {filename}
      </Text>
      <Box
        ref={lineCounterRef}
        h={SNIPPET_HEIGHT}
        w={57}
        overflowY="hidden"
        position="absolute"
        textAlign="right"
        bgColor={COLORS.grey06}
        borderTopLeftRadius={BORDER_RADIUS}
        borderBottomLeftRadius={BORDER_RADIUS}
        border="1px solid"
        borderColor={COLORS.grey05}
        py={4}
        pr={4}
      >
        {Array.from({ length: code.split('\n').length }, (_, rowIndex) => (
          // Performance is much better using "p" instead of "Text" from chakra. It is because of how many props the component has.
          <p style={{ ...TEXT_CODE_PROPS, color: COLORS.grey02 }} key={rowIndex}>
            {rowIndex + 1}
          </p>
        ))}
      </Box>
      {/* Not using "Textarea" from chakra because it has many styles which need to be overwritten. */}
      <textarea
        wrap="off"
        ref={textareaRef}
        onScroll={handleTextareaScroll}
        value={code}
        readOnly
        style={{
          ...TEXT_CODE_PROPS,
          height: SNIPPET_HEIGHT,
          width: '100%',
          resize: 'none',
          outline: 'none',
          paddingLeft: '4.5rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          backgroundColor: COLORS.grey07,
          color: COLORS.grey02,
          borderRadius: BORDER_RADIUS,
          border: '1px solid',
          borderColor: COLORS.grey06,
        }}
      />
    </Box>
  )
}
